import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import SongCard from '../components/SongCard';
import { usePlayer } from '../context/PlayerContext';
import { Button } from '../components/ui/button';
import { 
  Play, 
  ChevronRight, 
  Music, 
  Users, 
  TrendingUp,
  Headphones,
  Heart,
  Clock,
  Sparkles
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Home = () => {
  const [featuredSongs, setFeaturedSongs] = useState([]);
  const [trendingArtists, setTrendingArtists] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(true);
  const { playSong } = usePlayer();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [songsRes, playlistsRes] = await Promise.all([
        axios.get(`${API}/songs?limit=12`),
        axios.get(`${API}/playlists?limit=6`)
      ]);
      setFeaturedSongs(songsRes.data);
      setPlaylists(playlistsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const genres = [
    { name: 'Worship', icon: '🙏', color: 'from-purple-500 to-indigo-600' },
    { name: 'Gospel', icon: '✨', color: 'from-yellow-500 to-orange-500' },
    { name: 'Praise', icon: '🎵', color: 'from-pink-500 to-rose-500' },
    { name: 'Hymns', icon: '📖', color: 'from-emerald-500 to-teal-500' },
    { name: 'Contemporary', icon: '🎸', color: 'from-blue-500 to-cyan-500' },
    { name: 'Instrumental', icon: '🎹', color: 'from-violet-500 to-purple-500' },
  ];

  return (
    <Layout>
      <div className="pb-32">
        {/* Hero Section */}
        <section className="relative px-4 pt-4 pb-8">
          <div className="relative rounded-3xl overflow-hidden min-h-[280px] md:min-h-[360px]">
            <img 
              src="https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?w=1200&h=600&fit=crop"
              alt="Featured"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 to-transparent" />
            
            <div className="relative z-10 h-full min-h-[280px] md:min-h-[360px] flex flex-col justify-end p-6 md:p-10">
              <span className="inline-flex items-center gap-2 text-[#FFE500] text-sm font-semibold mb-3">
                <Sparkles className="h-4 w-4" />
                FEATURED TODAY
              </span>
              <h1 className="text-3xl md:text-5xl font-bold text-white mb-3 max-w-lg">
                Worship Without Limits
              </h1>
              <p className="text-white/70 text-sm md:text-base mb-5 max-w-md">
                Discover the latest gospel and worship music from artists around the world.
              </p>
              <div className="flex gap-3">
                <Link to="/discover">
                  <button className="px-6 py-3 rounded-full bg-[#FFE500] text-black flex items-center gap-2 font-bold shadow-lg shadow-[#FFE500]/20 hover:scale-105 transition-transform">
                    <Play className="h-5 w-5" fill="black" />
                    Explore
                  </button>
                </Link>
                <Link to="/register">
                  <button className="pill-btn pill-btn-inactive">
                    Join Free
                  </button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Genres */}
        <section className="px-4 mb-8">
          <h2 className="section-title">Browse by Genre</h2>
          <div className="horizontal-scroll">
            {genres.map((genre, index) => (
              <Link
                key={genre.name}
                to={`/discover?genre=${genre.name}`}
                className={`flex-shrink-0 w-32 h-32 rounded-2xl bg-gradient-to-br ${genre.color} p-4 flex flex-col justify-between transition-transform hover:scale-105`}
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <span className="text-3xl">{genre.icon}</span>
                <span className="text-white font-semibold text-sm">{genre.name}</span>
              </Link>
            ))}
          </div>
        </section>

        {/* Recently Played / Featured Songs */}
        <section className="px-4 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-white">Fresh Worship</h2>
            <Link to="/discover" className="text-[#FFE500] text-sm font-semibold flex items-center gap-1">
              See All <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
          
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-square bg-[#1E2028] rounded-2xl mb-3" />
                  <div className="h-4 bg-[#1E2028] rounded w-3/4 mb-2" />
                  <div className="h-3 bg-[#1E2028] rounded w-1/2" />
                </div>
              ))}
            </div>
          ) : featuredSongs.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {featuredSongs.slice(0, 6).map((song, index) => (
                <SongCard key={song.id} song={song} playlist={featuredSongs} index={index} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-[#16181D] rounded-2xl">
              <Music className="h-12 w-12 mx-auto mb-3 text-[#666]" />
              <p className="text-[#A0A0A0]">No songs yet. Be the first to upload!</p>
              <Link to="/register">
                <button className="pill-btn pill-btn-active mt-4">Upload Music</button>
              </Link>
            </div>
          )}
        </section>

        {/* Trending Songs (List View) */}
        {featuredSongs.length > 0 && (
          <section className="px-4 mb-8">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-[#FFE500]" />
                <h2 className="text-xl font-bold text-white">Trending Now</h2>
              </div>
            </div>
            
            <div className="bg-[#16181D] rounded-2xl p-2">
              {featuredSongs.slice(0, 5).map((song, index) => (
                <div 
                  key={song.id}
                  onClick={() => playSong(song, featuredSongs)}
                  className="song-row group"
                  data-testid={`trending-song-${song.id}`}
                >
                  <span className="w-6 text-center text-[#666] font-semibold">
                    {index + 1}
                  </span>
                  <div className="album-art album-art-sm relative">
                    <img 
                      src={song.cover_image || 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=100'}
                      alt={song.title}
                      className="w-full h-full object-cover rounded-lg"
                    />
                    <div className="play-btn-overlay rounded-lg">
                      <Play className="h-5 w-5 text-white" fill="white" />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-white truncate">{song.title}</p>
                    <p className="text-sm text-[#A0A0A0] truncate">{song.artist_name}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-[#666]">
                      {song.play_count?.toLocaleString() || 0} plays
                    </span>
                    <button className="like-btn text-[#666] hover:text-red-500 p-2">
                      <Heart className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Top Playlists */}
        {playlists.length > 0 && (
          <section className="px-4 mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-white">Community Playlists</h2>
              <Link to="/playlists" className="text-[#FFE500] text-sm font-semibold flex items-center gap-1">
                See All <ChevronRight className="h-4 w-4" />
              </Link>
            </div>
            
            <div className="horizontal-scroll">
              {playlists.map((playlist, index) => (
                <Link 
                  key={playlist.id}
                  to="/playlists"
                  className="flex-shrink-0 w-40"
                >
                  <div className="card-music p-0 overflow-hidden">
                    <div className="aspect-square bg-gradient-to-br from-[#FFE500]/20 to-purple-500/20 flex items-center justify-center">
                      <Headphones className="h-12 w-12 text-[#FFE500]" />
                    </div>
                    <div className="p-3">
                      <p className="font-semibold text-white truncate">{playlist.name}</p>
                      <p className="text-xs text-[#A0A0A0]">{playlist.songs?.length || 0} songs</p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Call to Action */}
        <section className="px-4 mb-8">
          <div className="bg-gradient-to-r from-[#FFE500]/10 to-purple-500/10 rounded-3xl p-6 md:p-10 border border-[#FFE500]/20">
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="flex-1">
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">
                  Share Your Music with the World
                </h3>
                <p className="text-[#A0A0A0] mb-5">
                  Join thousands of gospel artists on M4manna. Upload your worship music, 
                  connect with fans, and grow your ministry.
                </p>
                <div className="flex gap-3">
                  <Link to="/register">
                    <button className="pill-btn pill-btn-active">
                      Start Free
                    </button>
                  </Link>
                  <Link to="/verification">
                    <button className="pill-btn pill-btn-inactive">
                      Get Verified
                    </button>
                  </Link>
                </div>
              </div>
              <div className="w-full md:w-auto flex justify-center">
                <div className="relative">
                  <div className="w-32 h-32 rounded-full bg-[#FFE500]/20 flex items-center justify-center">
                    <Music className="h-16 w-16 text-[#FFE500]" />
                  </div>
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-[#FFE500] rounded-full flex items-center justify-center">
                    <TrendingUp className="h-4 w-4 text-black" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default Home;
